import { useState, useMemo, Fragment } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Tooltip,
  Paper,
  TextInput,
  Textarea,
  Table,
  Pagination,
  ActionIcon,
  Loader,
  Center,
  MultiSelect,
  Select,
  Collapse,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconCheck,
  IconX,
  IconFilter,
  IconRefresh,
  IconChevronDown,
  IconChevronRight,
} from "@tabler/icons-react";
import {
  useSourcesAdmin,
  useCreateSource,
  useUpdateSource,
  useToggleSourceActive,
} from "../../../hooks/admin/useSources";
import { useCollectionMethodsAdmin } from "../../../hooks/admin/useCollectionMethods";
import { useProceduresAdmin } from "../../../hooks/admin/useProceduresAdmin";
import {
  useSourceSubsites,
  useCreateSourceSubsite,
  useUpdateSourceSubsite,
  useToggleSourceSubsiteActive,
} from "../../../hooks/admin/useSourceSubsites";
import SourceForm, {
  SourceFormValues,
} from "../../../components/admin/sources/SourceForm";
import { Source } from "../../../services/admin/sources.service";
import { SourceSubsite } from "../../../services/admin/sourcesubsites.service";
import { required, scrollToFirstError } from "../../../utils/formValidation";

interface SourceFilters {
  collectionMethodIds?: string[];
  procedureIds?: string[];
  hasOrganMap?: "all" | "with" | "without";
  status?: "all" | "active" | "inactive";
}

interface SubsiteFormValues {
  name: string;
  description: string;
}

const ITEMS_PER_PAGE = 20;

export default function SourcesAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);
  const [
    subsiteModalOpened,
    { open: openSubsiteModal, close: closeSubsiteModal },
  ] = useDisclosure(false);
  const [
    subsiteConfirmModalOpened,
    { open: openSubsiteConfirmModal, close: closeSubsiteConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [editingSource, setEditingSource] = useState<Source | null>(null);
  const [sourceToToggle, setSourceToToggle] = useState<Source | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<SourceFilters>({});
  const [pendingFilters, setPendingFilters] = useState<SourceFilters>({});
  const [page, setPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Subsite state
  const [editingSubsite, setEditingSubsite] = useState<SourceSubsite | null>(
    null
  );
  const [subsiteSourceId, setSubsiteSourceId] = useState<string | null>(null);
  const [subsiteToToggle, setSubsiteToToggle] = useState<SourceSubsite | null>(
    null
  );

  // Data hooks
  const { data: sources = [], isLoading, refetch } = useSourcesAdmin();
  const { data: collectionMethods = [] } = useCollectionMethodsAdmin();
  const { data: procedures = [] } = useProceduresAdmin();
  const { data: allSubsites = [] } = useSourceSubsites();
  const createSource = useCreateSource();
  const updateSource = useUpdateSource();
  const toggleSourceActive = useToggleSourceActive();
  const createSubsite = useCreateSourceSubsite();
  const updateSubsite = useUpdateSourceSubsite();
  const toggleSubsiteActive = useToggleSourceSubsiteActive();

  // Group subsites by source ID
  const subsitesBySourceId = useMemo(() => {
    const grouped: Record<string, SourceSubsite[]> = {};
    allSubsites.forEach((subsite) => {
      if (!grouped[subsite.sourceId]) {
        grouped[subsite.sourceId] = [];
      }
      grouped[subsite.sourceId].push(subsite);
    });
    // Sort subsites by name within each group
    Object.keys(grouped).forEach((sourceId) => {
      grouped[sourceId].sort((a, b) => a.name.localeCompare(b.name));
    });
    return grouped;
  }, [allSubsites]);

  // Subsite form
  const subsiteForm = useForm<SubsiteFormValues>({
    initialValues: {
      name: "",
      description: "",
    },
    validate: {
      name: required("Subsite name is required"),
    },
  });

  // Filter data based on search and filters
  const filteredSources = useMemo(() => {
    let result = sources;

    // Apply search
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (source) =>
          source.name.toLowerCase().includes(searchLower) ||
          source.description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply collection method filter
    if (filters.collectionMethodIds && filters.collectionMethodIds.length > 0) {
      result = result.filter(
        (source) =>
          source.collectionMethodId &&
          filters.collectionMethodIds!.includes(source.collectionMethodId)
      );
    }

    // Apply procedure filter
    if (filters.procedureIds && filters.procedureIds.length > 0) {
      result = result.filter((source) => {
        if (
          !source.applicableProcedures ||
          source.applicableProcedures.length === 0
        ) {
          // Sources with no procedures set apply to all procedures, so they match any filter
          return true;
        }
        return filters.procedureIds!.some((pid) =>
          source.applicableProcedures!.includes(pid)
        );
      });
    }

    // Apply organ map filter
    if (filters.hasOrganMap === "with") {
      result = result.filter((source) => source.organMapTemplateId);
    } else if (filters.hasOrganMap === "without") {
      result = result.filter((source) => !source.organMapTemplateId);
    }

    // Apply status filter
    if (filters.status === "active") {
      result = result.filter((source) => source.isActive);
    } else if (filters.status === "inactive") {
      result = result.filter((source) => !source.isActive);
    }

    return result;
  }, [sources, debouncedSearch, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredSources.length / ITEMS_PER_PAGE);
  const paginatedSources = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredSources.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredSources, page]);

  // Check if any filters are active
  const hasActiveFilters =
    (filters.collectionMethodIds && filters.collectionMethodIds.length > 0) ||
    (filters.procedureIds && filters.procedureIds.length > 0) ||
    (filters.hasOrganMap && filters.hasOrganMap !== "all") ||
    (filters.status && filters.status !== "all");

  // Handlers
  const handleAdd = () => {
    setEditingSource(null);
    openFormModal();
  };

  const handleEdit = (source: Source) => {
    setEditingSource(source);
    openFormModal();
  };

  const handleSubmit = async (values: SourceFormValues) => {
    const sanitizedValues = {
      ...values,
      collectionMethodId: values.collectionMethodId || null,
    };

    if (editingSource) {
      await updateSource.mutateAsync({
        id: editingSource.id,
        ...sanitizedValues,
      });
    } else {
      await createSource.mutateAsync(sanitizedValues);
    }
    closeFormModal();
  };

  const handleToggleActive = (source: Source) => {
    setSourceToToggle(source);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (sourceToToggle) {
      await toggleSourceActive.mutateAsync({
        id: sourceToToggle.id,
        isActive: !sourceToToggle.isActive,
      });
      closeConfirmModal();
      setSourceToToggle(null);
    }
  };

  // Filter handlers
  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearPendingFilters = () => {
    setPendingFilters({});
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
  };

  // Row expansion
  const toggleRowExpanded = (sourceId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(sourceId)) {
        next.delete(sourceId);
      } else {
        next.add(sourceId);
      }
      return next;
    });
  };

  // Subsite handlers
  const handleAddSubsite = (sourceId: string) => {
    setEditingSubsite(null);
    setSubsiteSourceId(sourceId);
    subsiteForm.reset();
    openSubsiteModal();
  };

  const handleEditSubsite = (subsite: SourceSubsite) => {
    setEditingSubsite(subsite);
    setSubsiteSourceId(subsite.sourceId);
    subsiteForm.setValues({
      name: subsite.name,
      description: subsite.description || "",
    });
    openSubsiteModal();
  };

  const handleSubsiteSubmit = async (values: SubsiteFormValues) => {
    if (editingSubsite) {
      await updateSubsite.mutateAsync({
        id: editingSubsite.id,
        ...values,
      });
    } else if (subsiteSourceId) {
      await createSubsite.mutateAsync({
        ...values,
        sourceId: subsiteSourceId,
      });
    }
    closeSubsiteModal();
    subsiteForm.reset();
  };

  const handleToggleSubsiteActive = (subsite: SourceSubsite) => {
    setSubsiteToToggle(subsite);
    openSubsiteConfirmModal();
  };

  const confirmToggleSubsiteActive = async () => {
    if (subsiteToToggle) {
      await toggleSubsiteActive.mutateAsync({
        id: subsiteToToggle.id,
        isActive: !subsiteToToggle.isActive,
      });
      closeSubsiteConfirmModal();
      setSubsiteToToggle(null);
    }
  };

  // Dropdown options
  const collectionMethodOptions = collectionMethods
    .filter((cm) => cm.isActive !== false)
    .map((cm) => ({ value: cm.id, label: cm.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const procedureOptions = procedures
    .filter((p) => p.isActive !== false)
    .map((p) => ({ value: p.id, label: p.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Sources & Subsites</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage specimen sources and their anatomical subsites
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add Source
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by name or description..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.collectionMethodIds &&
                      filters.collectionMethodIds.length > 0 && (
                        <Badge variant="light" color="blue">
                          {filters.collectionMethodIds.length} Collection Method
                          {filters.collectionMethodIds.length > 1 ? "s" : ""}
                        </Badge>
                      )}
                    {filters.procedureIds &&
                      filters.procedureIds.length > 0 && (
                        <Badge variant="light" color="cyan">
                          {filters.procedureIds.length} Procedure
                          {filters.procedureIds.length > 1 ? "s" : ""}
                        </Badge>
                      )}
                    {filters.hasOrganMap && filters.hasOrganMap !== "all" && (
                      <Badge variant="light" color="violet">
                        {filters.hasOrganMap === "with"
                          ? "Has Organ Map"
                          : "No Organ Map"}
                      </Badge>
                    )}
                    {filters.status && filters.status !== "all" && (
                      <Badge variant="light" color="green">
                        {filters.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredSources.length} source
                  {filteredSources.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Sources ({filteredSources.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, filteredSources.length)}{" "}
                      of {filteredSources.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredSources.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || search
                    ? "No sources match the current filters"
                    : "No sources found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 40 }} />
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Default Collection Method</Table.Th>
                      <Table.Th>Organ Map</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedSources.map((source) => {
                      const isExpanded = expandedRows.has(source.id);
                      const subsites = subsitesBySourceId[source.id] || [];
                      return (
                        <Fragment key={source.id}>
                          <Table.Tr
                            style={{
                              opacity: source.isActive ? 1 : 0.5,
                            }}
                          >
                            {/* Expand toggle */}
                            <Table.Td>
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                onClick={() => toggleRowExpanded(source.id)}
                              >
                                {isExpanded ? (
                                  <IconChevronDown size={16} />
                                ) : (
                                  <IconChevronRight size={16} />
                                )}
                              </ActionIcon>
                            </Table.Td>

                            {/* Name */}
                            <Table.Td>
                              <Group gap="xs">
                                <Text size="sm" fw={500}>
                                  {source.name}
                                </Text>
                                {subsites.length > 0 && (
                                  <Badge size="xs" variant="light" color="gray">
                                    {subsites.length} subsite
                                    {subsites.length !== 1 ? "s" : ""}
                                  </Badge>
                                )}
                              </Group>
                            </Table.Td>

                            {/* Default Collection Method */}
                            <Table.Td>
                              <Badge
                                color={
                                  source.collectionMethodId ? "blue" : "gray"
                                }
                                variant="light"
                              >
                                {source.collectionMethod?.name || "Not Set"}
                              </Badge>
                            </Table.Td>

                            {/* Organ Map */}
                            <Table.Td>
                              <Badge
                                color={
                                  source.organMapTemplateId ? "violet" : "gray"
                                }
                                variant="light"
                              >
                                {source.organMapTemplate?.name || "Not Set"}
                              </Badge>
                            </Table.Td>

                            {/* Status */}
                            <Table.Td>
                              <Badge
                                color={source.isActive ? "green" : "red"}
                                variant="light"
                                size="sm"
                              >
                                {source.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </Table.Td>

                            {/* Actions */}
                            <Table.Td>
                              <Group gap="xs">
                                <Tooltip label="Edit">
                                  <ActionIcon
                                    variant="subtle"
                                    color="blue"
                                    onClick={() => handleEdit(source)}
                                  >
                                    <IconEdit size={18} />
                                  </ActionIcon>
                                </Tooltip>
                                <Tooltip
                                  label={
                                    source.isActive ? "Deactivate" : "Activate"
                                  }
                                >
                                  <ActionIcon
                                    variant="subtle"
                                    color={source.isActive ? "red" : "green"}
                                    onClick={() => handleToggleActive(source)}
                                  >
                                    {source.isActive ? (
                                      <IconX size={18} />
                                    ) : (
                                      <IconCheck size={18} />
                                    )}
                                  </ActionIcon>
                                </Tooltip>
                              </Group>
                            </Table.Td>
                          </Table.Tr>

                          {/* Expanded Row - Subsites */}
                          <Table.Tr>
                            <Table.Td colSpan={6} p={0}>
                              <Collapse in={isExpanded}>
                                <Box p="md" bg="gray.0">
                                  <Stack gap="sm">
                                    <Group justify="space-between">
                                      <Text size="sm" fw={500}>
                                        Subsites for {source.name}
                                      </Text>
                                      <Button
                                        size="xs"
                                        variant="light"
                                        leftSection={<IconPlus size={14} />}
                                        onClick={() =>
                                          handleAddSubsite(source.id)
                                        }
                                      >
                                        Add Subsite
                                      </Button>
                                    </Group>

                                    {subsites.length === 0 ? (
                                      <Text size="sm" c="dimmed" ta="center">
                                        No subsites defined for this source
                                      </Text>
                                    ) : (
                                      <Table>
                                        <Table.Thead>
                                          <Table.Tr>
                                            <Table.Th>Subsite Name</Table.Th>
                                            <Table.Th>Description</Table.Th>
                                            <Table.Th style={{ width: 80 }}>
                                              Status
                                            </Table.Th>
                                            <Table.Th style={{ width: 80 }}>
                                              Actions
                                            </Table.Th>
                                          </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                          {subsites.map((subsite) => (
                                            <Table.Tr
                                              key={subsite.id}
                                              style={{
                                                opacity: subsite.isActive
                                                  ? 1
                                                  : 0.5,
                                              }}
                                            >
                                              <Table.Td>
                                                <Text size="sm" fw={500}>
                                                  {subsite.name}
                                                </Text>
                                              </Table.Td>
                                              <Table.Td>
                                                <Text size="sm">
                                                  {subsite.description || "—"}
                                                </Text>
                                              </Table.Td>
                                              <Table.Td>
                                                <Badge
                                                  color={
                                                    subsite.isActive
                                                      ? "green"
                                                      : "red"
                                                  }
                                                  variant="light"
                                                  size="xs"
                                                >
                                                  {subsite.isActive
                                                    ? "Active"
                                                    : "Inactive"}
                                                </Badge>
                                              </Table.Td>
                                              <Table.Td>
                                                <Group gap="xs">
                                                  <Tooltip label="Edit">
                                                    <ActionIcon
                                                      variant="subtle"
                                                      color="blue"
                                                      size="sm"
                                                      onClick={() =>
                                                        handleEditSubsite(
                                                          subsite
                                                        )
                                                      }
                                                    >
                                                      <IconEdit size={14} />
                                                    </ActionIcon>
                                                  </Tooltip>
                                                  <Tooltip
                                                    label={
                                                      subsite.isActive
                                                        ? "Deactivate"
                                                        : "Activate"
                                                    }
                                                  >
                                                    <ActionIcon
                                                      variant="subtle"
                                                      color={
                                                        subsite.isActive
                                                          ? "red"
                                                          : "green"
                                                      }
                                                      size="sm"
                                                      onClick={() =>
                                                        handleToggleSubsiteActive(
                                                          subsite
                                                        )
                                                      }
                                                    >
                                                      {subsite.isActive ? (
                                                        <IconX size={14} />
                                                      ) : (
                                                        <IconCheck size={14} />
                                                      )}
                                                    </ActionIcon>
                                                  </Tooltip>
                                                </Group>
                                              </Table.Td>
                                            </Table.Tr>
                                          ))}
                                        </Table.Tbody>
                                      </Table>
                                    )}
                                  </Stack>
                                </Box>
                              </Collapse>
                            </Table.Td>
                          </Table.Tr>
                        </Fragment>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Add/Edit Source Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={editingSource ? "Edit Source" : "Add New Source"}
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <SourceForm
              initialValues={editingSource || undefined}
              onSubmit={handleSubmit}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createSource.isPending || updateSource.isPending}
                onClick={() => {
                  const form = document.querySelector("form");
                  if (form) form.requestSubmit();
                }}
              >
                {editingSource ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Add/Edit Subsite Modal */}
        <Modal
          opened={subsiteModalOpened}
          onClose={closeSubsiteModal}
          title={editingSubsite ? "Edit Subsite" : "Add New Subsite"}
          size="md"
          closeOnClickOutside={false}
        >
          <form
            onSubmit={subsiteForm.onSubmit(handleSubsiteSubmit, () =>
              scrollToFirstError(subsiteForm)
            )}
          >
            <Stack gap="md">
              <TextInput
                label="Subsite Name"
                placeholder="e.g., Ascending, Cecum, Antrum"
                withAsterisk
                {...subsiteForm.getInputProps("name")}
              />

              <Textarea
                label="Description"
                placeholder="Enter subsite description"
                minRows={3}
                {...subsiteForm.getInputProps("description")}
              />

              {/* Parent Source - Read-only, pre-selected from expanded row */}
              <TextInput
                label="Parent Source"
                value={
                  editingSubsite
                    ? editingSubsite.source?.name ||
                      sources.find((s) => s.id === editingSubsite.sourceId)
                        ?.name ||
                      ""
                    : sources.find((s) => s.id === subsiteSourceId)?.name || ""
                }
                disabled
                readOnly
              />

              <Group justify="flex-end" mt="md">
                <Button variant="subtle" onClick={closeSubsiteModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={createSubsite.isPending || updateSubsite.isPending}
                >
                  {editingSubsite ? "Update" : "Create"}
                </Button>
              </Group>
            </Stack>
          </form>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Sources"
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <MultiSelect
              label="Collection Methods"
              placeholder="Select collection methods..."
              data={collectionMethodOptions}
              value={pendingFilters.collectionMethodIds || []}
              onChange={(values) =>
                setPendingFilters({
                  ...pendingFilters,
                  collectionMethodIds: values.length > 0 ? values : undefined,
                })
              }
              searchable
              clearable
            />

            <MultiSelect
              label="Applicable Procedures"
              placeholder="Select procedures..."
              description="Show sources that apply to these procedures"
              data={procedureOptions}
              value={pendingFilters.procedureIds || []}
              onChange={(values) =>
                setPendingFilters({
                  ...pendingFilters,
                  procedureIds: values.length > 0 ? values : undefined,
                })
              }
              searchable
              clearable
            />

            <Select
              label="Organ Map"
              placeholder="All"
              data={[
                { value: "all", label: "All" },
                { value: "with", label: "With Organ Map" },
                { value: "without", label: "Without Organ Map" },
              ]}
              value={pendingFilters.hasOrganMap || "all"}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  hasOrganMap:
                    (value as SourceFilters["hasOrganMap"]) || undefined,
                })
              }
              clearable
            />

            <Select
              label="Status"
              placeholder="All statuses"
              data={[
                { value: "all", label: "All" },
                { value: "active", label: "Active Only" },
                { value: "inactive", label: "Inactive Only" },
              ]}
              value={pendingFilters.status || "all"}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  status: (value as SourceFilters["status"]) || undefined,
                })
              }
              clearable
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={handleClearPendingFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Confirmation Modal for Activate/Deactivate Source */}
        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title={
            sourceToToggle?.isActive ? "Deactivate Source" : "Activate Source"
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {sourceToToggle?.isActive ? "deactivate" : "activate"} the source{" "}
              <strong>{sourceToToggle?.name}</strong>?
            </Text>
            {sourceToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivated sources will no longer be available for selection.
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color={sourceToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggleActive}
                loading={toggleSourceActive.isPending}
              >
                {sourceToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Confirmation Modal for Activate/Deactivate Subsite */}
        <Modal
          opened={subsiteConfirmModalOpened}
          onClose={closeSubsiteConfirmModal}
          title={
            subsiteToToggle?.isActive
              ? "Deactivate Subsite"
              : "Activate Subsite"
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {subsiteToToggle?.isActive ? "deactivate" : "activate"} the
              subsite <strong>{subsiteToToggle?.name}</strong>?
            </Text>
            {subsiteToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivated subsites will no longer be available for selection.
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeSubsiteConfirmModal}>
                Cancel
              </Button>
              <Button
                color={subsiteToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggleSubsiteActive}
                loading={toggleSubsiteActive.isPending}
              >
                {subsiteToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
